import {useCallback, useEffect, useState} from "react";
import {useKeyedSingleFlight} from "utils/useSingleFlight";
import {useAuthenticatedSession, useIsLoggedIn} from "UserProvider";
import {authenticatedCreationAction} from "home/transferCreationAuth";
import {SponsorApi} from "./SponsorApi";
import {findMyBookingRow, toMyBookingRows, withWithdrawnBooking, type MyBookingRow} from "./myBookings";
import {checkoutRefusalKey, myBookingsErrorKey, type SponsorErrorKey} from "./sponsorErrors";

/**
 * The advertiser's own bookings on `/advertise` (epic #441), including payment and consumer withdrawal of
 * an approved booking (kitecorp/transferify-api-service#1362).
 *
 * A thin adapter (epic #26): what a row means is `myBookings.ts`, what a failure means is
 * `sponsorErrors.ts`, and who may ask is `authenticatedCreationAction` — the verdict the booking submit
 * and the reminder upload act on once the session has answered (`settledCreationAction`). Sharing it is
 * the point: the list appears exactly when a submit would be accepted, so the page never shows a list to
 * someone it would then refuse to serve, nor hides one from someone it would.
 *
 * The effect re-runs when that admission flips, which is what makes the list appear by itself once the
 * sign-in modal closes — no reload, and no second "did they sign in yet?" rule anywhere. The same flip
 * is how the list WAITS for a restored session the way a press does (ui#3117): nothing is read while it
 * is Pending, and the list loads once it is Authenticated. The list is not a press, so nothing here ever
 * asks anyone to sign in.
 */
export interface UseMyBookings {
	/** The advertiser's bookings, newest first; empty for a signed-out visitor. */
	bookings: readonly MyBookingRow[];
	/** True while a load is in flight. False for a signed-out visitor, who has nothing to wait for. */
	loading: boolean;
	/** `m.sponsor.errors[key]`, or `null` when there is nothing to say. */
	error: SponsorErrorKey | null;
	/** Whether the section has anything at all to render — signed in, and not empty-handed. */
	visible: boolean;
	/**
	 * The booking whose checkout is being opened, or `null`.
	 *
	 * The ID rather than a boolean, so only the pressed row's own button says "Taking you to checkout…"
	 * while the rest of the list stays exactly as it was.
	 */
	payingBookingId: string | null;
	markWithdrawn: (bookingId: string) => void;
	/**
	 * Opens Stripe Checkout for one approved, unpaid booking and hands the browser to it.
	 *
	 * `navigate` is injectable for the same reason the booking form makes it injectable: a full-page
	 * assignment to `window.location` is untestable and would tear down the test runner's document, so
	 * the ONE line that leaves the app is a seam.
	 */
	payNow: (bookingId: string, navigate?: (url: string) => void) => Promise<void>;
}

/**
 * The single-flight key. One key for the whole list, not one per booking: see {@link useMyBookings}.
 */
const CHECKOUT_FLIGHT = "checkout";

/** Assigning `window.location` is what actually leaves for Stripe; injectable so tests never do. */
const REDIRECT = (url: string) => {
	window.location.href = url;
};

export function useMyBookings(): UseMyBookings {
	const isLoggedIn = useIsLoggedIn();
	const [authenticatedSession] = useAuthenticatedSession();
	const [bookings, setBookings] = useState<readonly MyBookingRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<SponsorErrorKey | null>(null);
	const [payingBookingId, setPayingBookingId] = useState<string | null>(null);

	/*
	 * Keyed per ACTION, exactly as `useSponsorBooking` keys its own, and NOT per booking id.
	 *
	 * The resource this protects is the BROWSER'S LOCATION, which the whole list shares: two rows
	 * pressed in quick succession would otherwise mint two Checkout Sessions and race two full-page
	 * redirects, and the advertiser would land on a payment page for whichever won. Keying by booking
	 * id would collapse a hammered single row and leave exactly that defect open.
	 *
	 * Synchronous, which is what makes it win the race a `disabled` prop loses: `payingBookingId` only
	 * reaches the button one render after the click.
	 */
	const flight = useKeyedSingleFlight();

	const admitted = authenticatedCreationAction(isLoggedIn, authenticatedSession) === "create";

	useEffect(() => {
		if (!admitted) {
			// Drop whatever the previous session loaded. A logout must not leave one advertiser's
			// campaigns on screen for whoever signs in next on this browser.
			setBookings([]);
			setError(null);
			setLoading(false);
			return;
		}
		let active = true;
		setLoading(true);
		SponsorApi.getMyBookings()
			.then(loaded => {
				if (active) {
					setBookings(toMyBookingRows(loaded));
					setError(null);
				}
			})
			.catch(e => {
				if (active) {
					setBookings([]);
					setError(myBookingsErrorKey(e));
				}
			})
			.finally(() => {
				if (active) {
					setLoading(false);
				}
			});
		return () => {
			active = false;
		};
	}, [admitted]);

	const payNow = useCallback((
		bookingId: string, navigate: (url: string) => void = REDIRECT,
	): Promise<void> => flight.run(CHECKOUT_FLIGHT, async () => {
		setPayingBookingId(bookingId);
		setError(null);
		try {
			const {checkoutUrl} = await SponsorApi.startCheckout(bookingId);
			/*
			 * `payingBookingId` is deliberately NOT cleared on success: the browser is already leaving
			 * for Stripe, and re-enabling the button under a departing page invites a second session.
			 */
			navigate(checkoutUrl);
		} catch (e) {
			/*
			 * A refusal means our copy of this booking is out of date. That is the ONLY way the api
			 * refuses a button this ui offers, so the list is re-read and the sentence is chosen from
			 * the row that comes back. Choosing it from the stale row would explain the refusal using
			 * exactly the state that caused it.
			 *
			 * A failed re-read leaves the list alone and answers with no row, which `checkoutRefusalKey`
			 * reads as the generic sentence: better than blanking a list because a second request also
			 * failed.
			 */
			const fresh = await SponsorApi.getMyBookings().then(toMyBookingRows).catch(() => null);
			if (fresh !== null) {
				setBookings(fresh);
			}
			setError(checkoutRefusalKey(e, findMyBookingRow(fresh ?? [], bookingId)));
			setPayingBookingId(null);
		}
	}), [flight]);

	/*
	 * The advertiser came BACK from Stripe without paying.
	 *
	 * `payingBookingId` is deliberately never cleared on the success path, because the browser is
	 * leaving; a back button returns to this page with that row still saying "Taking you to checkout"
	 * and its button dead. Restoring from the bfcache fires `pageshow` with `persisted` true and runs
	 * no effect that would otherwise reset it, so this is the one signal that the page is being shown
	 * again rather than loaded again.
	 */
	useEffect(() => {
		const restored = (event: PageTransitionEvent) => {
			if (event.persisted) {
				setPayingBookingId(null);
			}
		};
		window.addEventListener("pageshow", restored);
		return () => window.removeEventListener("pageshow", restored);
	}, []);

	return {
		bookings,
		loading,
		error,
		// A signed-out visitor is shown nothing rather than an empty state: "you have no bookings" is a
		// claim about an account, and there isn't one.
		visible: admitted,
		payingBookingId,
		payNow,
		markWithdrawn: bookingId => setBookings(rows => withWithdrawnBooking(rows, bookingId)),
	};
}
